import { observer } from 'mobx-react';
import React, { FC, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { components, OptionProps, MultiValueRemoveProps, MenuListProps, GroupBase, MultiValue, ActionMeta, PropsValue } from 'react-select';
import { AsyncPaginate, wrapMenuList } from 'react-select-async-paginate';
import { RootStoreContext } from '@stores/rootStore';
import { Scrollbars } from 'rc-scrollbars';
import { LFLabel, splitScopedLabel } from '@components/LFLabel';
import Link from 'next/link';
import { isPresent } from '@farmai-shared/LFUtils';
import { Check, Plus } from '@components/icons';
import { LFFieldWrapper } from '@app/_components/form-control/helpers';
import { ILabel, ILabelSnapshotIn } from '@stores/LabelStore';
import { createAsyncOptionLoader } from './createAsyncOptionLoader';
interface ILabelOption {
  label: string;
  value: string;
  color: string;
  readOnly: boolean;
  organizationId: string;
}
const defaultDropdownStyles = {
  control: (provided, state) => ({
    ...provided,
    border: 0,
    height: '100%',
    background: 'transparent',
    boxShadow: 'none',
    cursor: 'pointer',
    ...(state.isDisabled && {
      pointerEvents: 'auto',
      cursor: 'not-allowed'
    })
  }),
  option: (provided, state) => ({
    ...provided,
    color: 'inherit',
    background: 'inherit',
    '&:hover': {
      background: '#F7F8FB'
    },
    padding: '1.5rem 0px',
    fontWeight: state.isSelected ? '400' : '300',
    cursor: 'pointer',
    fontSize: '1.6rem'
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: 'transparent'
  }),
  placeholder: provided => ({
    ...provided,
    color: '#1C1F28',
    fontSize: '1.6rem'
  }),
  multiValueRemove: () => ({}),
  valueContainer: provided => ({
    ...provided,
    fontSize: '1.6rem'
  })
};
const CustomMenuList = observer((props: MenuListProps<ILabelOption, true, GroupBase<ILabelOption>>) => {
  const intermediateRef = useRef<Scrollbars | null>(null);
  const {
    t
  } = useTranslation();
  useEffect(() => {
    if (intermediateRef.current?.view && typeof props.innerRef === 'function') {
      props.innerRef(intermediateRef.current.view as HTMLDivElement);
    }
  }, [props.innerRef, intermediateRef]);
  return <components.MenuList {...props}>
        <Scrollbars className="custom-scrollbar" autoHeight ref={intermediateRef}>
          {props.children}
        </Scrollbars>
        <div className="mx-8 border-b border-gray-200" />
        <Link target="_blank" className="cursor-default" href={`/settings/labels`}>
          <p className={`
              mx-12 w-fit cursor-pointer py-6 text-2xl capitalize
              hover:text-primary-300
            `}>
            {t('manageLabels')}
          </p>
        </Link>
      </components.MenuList>;
});
const MenuList = wrapMenuList(CustomMenuList);
const ControlComponent = observer(props => {
  const {
    t
  } = useTranslation();
  const {
    onMenuOpen
  } = props.selectProps;
  return <label className="flex h-full flex-col text-xl text-grey">
      <div className="flex justify-between capitalize">
        <p>{t('labels')}</p>
        <div onClick={onMenuOpen} className="flex cursor-pointer items-center gap-x-1 font-medium text-secondary-300">
          <Plus className="w-4" />
          <p>{t('assignLabels')}</p>
        </div>
      </div>

      <components.Control {...props} />
    </label>;
});
const Option: FC<OptionProps<ILabelOption, true, GroupBase<ILabelOption>>> = observer(props => {
  const {
    isSelected,
    label: name,
    data
  } = props;
  return <components.Option {...props}>
        <div className="mx-12 flex items-center gap-x-4">
          <div className={isSelected ? 'visible' : 'invisible'}>
            <Check />
          </div>
          <div style={{
        backgroundColor: data.color
      }} className="h-6 w-6 rounded-md" />
          <p>{name}</p>
        </div>
      </components.Option>;
});
const MultiValueLabel: FC<MultiValueRemoveProps<ILabelOption, true, GroupBase<ILabelOption>>> = observer(props => {
  const {
    data,
    innerProps
  } = props;
  return <components.MultiValueLabel {...props}>
      <LFLabel label={{
      name: data.label,
      color: data.color,
      id: data.value,
      readOnly: data.readOnly,
      organizationId: data.organizationId
    }} {...innerProps.onClick && {
      onClickRemove: innerProps.onClick as () => void
    }} />
    </components.MultiValueLabel>;
});
export const LFFormLabelDropdown: FC<{
  value: PropsValue<ILabelOption> | undefined;
  onChange: (value: PropsValue<ILabelOption> | undefined) => void;
  inputName: string;
}> = observer(({
  value,
  onChange,
  inputName
}) => {
  const {
    uiStore
  } = useContext(RootStoreContext);
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const {
    t
  } = useTranslation();
  const handleMouseOut = () => {
    timeout.current = setTimeout(() => setMenuIsOpen(false), 2000);
  };
  const handleMouseEnter = () => timeout.current && clearTimeout(timeout.current);
  useEffect(() => {
    const dropdown = document.getElementById('labels-dropdown');
    dropdown?.addEventListener('mouseleave', handleMouseOut);
    dropdown?.addEventListener('mouseenter', handleMouseEnter);
    return () => {
      if (dropdown) {
        dropdown.removeEventListener('mouseleave', handleMouseOut);
        dropdown.removeEventListener('mouseenter', handleMouseEnter);
      }
    };
  }, []);
  const loadOptions = useMemo(() => createAsyncOptionLoader<ILabelSnapshotIn, ILabelOption>(({
    search,
    page
  }) => uiStore.currentOrganization!.fetchLabels({
    search: search,
    page: page
  }), o => ({
    label: o.name,
    value: o.id,
    color: o.color,
    readOnly: o.readOnly,
    organizationId: o.organizationId
  })), []);
  return <div id="labels-dropdown" className="h-full w-full">
      <AsyncPaginate<ILabelOption, GroupBase<ILabelOption>, {
      page: number;
    }, true> name={inputName} value={value} isMulti isSearchable loadOptions={loadOptions} placeholder={t('none')} additional={{
      page: 1
    }} menuIsOpen={menuIsOpen} onMenuOpen={() => setMenuIsOpen(true)} styles={defaultDropdownStyles} loadingMessage={() => ''} // null removes loading message, blank keeps the box on initial press and shows our default loading indicator
    onChange={(val: MultiValue<ILabelOption>, change: ActionMeta<ILabelOption>) => {
      if (change.action === 'select-option') {
        const {
          scope
        } = splitScopedLabel(change.option?.label);
        if (scope) {
          const re = new RegExp(`${scope}::`);
          return onChange(val?.filter(v => !re.test(v.label) || v.label === change.option?.label));
        }
      }
      onChange(val);
    }} hideSelectedOptions={false} onBlur={() => setMenuIsOpen(false)} onFocus={() => setMenuIsOpen(true)} components={{
      Control: ControlComponent,
      DropdownIndicator: () => null,
      IndicatorSeparator: () => null,
      LoadingIndicator: () => null,
      MenuList: MenuList,
      Option: Option,
      MultiValueLabel: () => null,
      MultiValueRemove: MultiValueLabel // backwards but needed to access clear function
    }} />
    </div>;
});